/* eslint-disable camelcase */
import React, { useCallback, useEffect } from 'react';
import Select from '@components/Atoms/Select';
import Button from '@components/Atoms/Button';
import TextField from '@components/Atoms/TextField';
import ShowIf from '@components/Atoms/ShowIf';
import Box from '@components/Atoms/Box';
import { CircularProgress, Checkbox, FormControlLabel } from '@mui/material';
import Typography from '@components/Atoms/Typography';
import Tooltip from '@components/Atoms/Tooltip';
import ShadowScrollbar from '@components/Scrollbar/ShadowScrollbars';
import {
  useContentFilterContext,
  useContentFilterElement,
  useContentFilterStandardContext,
} from '@components/ContentFilters/ContentFilterProvider';
import { useParams } from 'react-router-dom';
import BloomsSelector from '@components/Selectors/BloomsSelector/BloomsSelector';
import DokSelector from '@components/Selectors/DokSelector/DokSelector';
import { useGetSectionSubjectUnitLesson } from '../../hooks/useGetSectionSubjectUnitLesson';
import { MenuRowWrapper, MenuColumn, MenuTwoRowWrapper } from './styles';
import { QuestionGenerationMenuType } from './types';
import QuestionTypeSelector from './QuestionTypeSelector';
import { cleanText } from './utils/helpers';

const toolTipStyle = {
  tooltip: {
    sx: {
      fontSize: '0.85rem',
      maxWidth: '500px',
    },
  },
};

const questionQuantityOptions = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((value) => ({
  value,
  label: value.toString(),
}));

export const invalidContentFilterMessage =
  'Please select at least one standard (and preferably a skill) to generate questions';

const QuestionGenerationMenu = ({
  submitCallback,
  customQuestionCallback,
  resetCallback,
  hasTask,
  isLoading,
  state,
  dispatch,
  isSubmitting,
  hasItems,
  generating,
  setPassageMode,
  coauthorEnabled,
}) => {
  const isDisabled = hasTask || isLoading || hasItems;
  const { subjectId } = useParams();
  const {
    subjects,
    selectedPassage,
    setSelectedPassage,
    isPassageAllowed,
    selectedBlooms,
    selectedDok,
  } = useContentFilterContext();
  const { selectedStandards } = useContentFilterStandardContext();
  const hasContentFilter = selectedStandards.length > 0;
  const ContentFilterElement = useContentFilterElement();
  const contentFilterRef = React.useRef<HTMLDivElement>(null);
  const { curriculumSubjects } = useGetSectionSubjectUnitLesson();

  const handleChange = useCallback(
    (type: QuestionGenerationMenuType) => (value: unknown) => {
      dispatch({ type, payload: value });
    },
    [dispatch],
  );

  useEffect(() => {
    if (selectedBlooms.length) {
      handleChange(QuestionGenerationMenuType.SET_BLOOMS)(selectedBlooms[0]);
    }

    if (selectedDok.length) {
      handleChange(QuestionGenerationMenuType.SET_DOK)(selectedDok[0]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <ShadowScrollbar
      style={{
        width: '100%',
        height: 'calc(75vh)',
      }}
    >
      <Box
        sx={{
          pr: '1rem',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          minWidth: '100%',
        }}
      >
        <Typography variant="subtitle1" component="p" sx={{ mb: '0.5rem' }}>
          Selections will be applied to all generated and/or custom questions.
        </Typography>
        <ShowIf If={!subjectId}>
          <Tooltip
            title="Select the subject you want to generate a question for."
            arrow
            placement="right"
            componentsProps={toolTipStyle}
            slotProps={{ popper: { disablePortal: true } }}
          >
            <MenuRowWrapper>
              <MenuColumn>
                <Select
                  id="select-subject"
                  onChangeHandler={(value: number) => {
                    const selected = curriculumSubjects.find(
                      ({ subject }) => subject?.id === value,
                    );
                    if (selected) {
                      handleChange(QuestionGenerationMenuType.SET_SUBJECT)(
                        selected.subject,
                      );
                    }
                  }}
                  defaultValue={subjects?.[0]?.id}
                  value={state.subject?.id}
                  fullWidth
                  list={
                    subjects?.map((subject) => ({
                      value: subject.id,
                      label: subject.name,
                    })) ?? []
                  }
                  label="Subject"
                  size="small"
                  disabled={isDisabled}
                />
              </MenuColumn>
            </MenuRowWrapper>
          </Tooltip>
        </ShowIf>
        <MenuRowWrapper>
          <MenuColumn>
            {ContentFilterElement && (
              <ContentFilterElement
                orientation="vertical"
                disabled={isDisabled}
                ref={contentFilterRef}
                exclude={['blooms', 'dok', 'questionType', 'readability', 'grade']}
              />
            )}
          </MenuColumn>
        </MenuRowWrapper>
        <MenuRowWrapper>
          <MenuColumn>
            <BloomsSelector
              value={[state.blooms]}
              onChange={(values) => {
                const selected = values?.[0] ?? state.blooms;
                handleChange(QuestionGenerationMenuType.SET_BLOOMS)(selected);
              }}
              disabled={isDisabled}
            />
          </MenuColumn>
        </MenuRowWrapper>
        <MenuRowWrapper>
          <MenuColumn>
            <DokSelector
              value={[state.dok]}
              onChange={(values) => {
                const selected = values?.[0] ?? state.dok;
                handleChange(QuestionGenerationMenuType.SET_DOK)(selected);
              }}
              disabled={isDisabled}
            />
          </MenuColumn>
        </MenuRowWrapper>
        <MenuRowWrapper>
          <QuestionTypeSelector
            handleChange={handleChange(QuestionGenerationMenuType.SET_QUESTION_TYPE)}
            isDisabled={isDisabled}
            selectedSubject={state.subject}
          />
        </MenuRowWrapper>
        <MenuRowWrapper>
          <ShowIf If={isPassageAllowed(state.subject) && coauthorEnabled}>
            <ShowIf If={selectedPassage?.data}>
              <TextField
                variant="outlined"
                label="Passage"
                multiline
                rows={4}
                value={cleanText(selectedPassage?.data?.content || '')}
                onChange={undefined}
                fullWidth
                disabled
                data-testid="passage-textfield"
              />
            </ShowIf>
            <Box sx={{ display: 'flex', gap: 2, width: '100%' }}>
              <Button
                variant="outlined"
                fullWidth
                disabled={isDisabled}
                sx={{ mt: '0.5rem' }}
                onClick={setPassageMode}
              >
                {selectedPassage?.data?.content ? 'Change Passage' : 'Select Passage'}
              </Button>
              <ShowIf If={selectedPassage?.data}>
                <Button
                  variant="outlined"
                  fullWidth
                  disabled={isDisabled}
                  sx={{ mt: '0.5rem' }}
                  onClick={() => setSelectedPassage(null)}
                >
                  Unset Passage
                </Button>
              </ShowIf>
            </Box>
          </ShowIf>
        </MenuRowWrapper>
        <MenuTwoRowWrapper>
          <MenuColumn>
            <Select
              id="select-qcount"
              onChangeHandler={handleChange(
                QuestionGenerationMenuType.SET_QUESTION_QUANTITY,
              )}
              value={state.questionQuantity}
              defaultValue={state.questionQuantity}
              fullWidth
              size="small"
              list={questionQuantityOptions}
              label="Questions to Generate with AI"
              disabled={generating !== null}
              data-testid="question-quantity-select"
            />
          </MenuColumn>
          <MenuColumn>
            <FormControlLabel
              label="Include Lesson Slides"
              labelPlacement="end"
              slotProps={{
                typography: {
                  fontSize: '0.75rem',
                },
              }}
              control={
                <Checkbox
                  data-testid="include-slides-checkbox"
                  disabled={isDisabled}
                  onChange={(e) =>
                    handleChange(QuestionGenerationMenuType.SET_INCLUDE_SLIDES)(
                      e.target.checked,
                    )
                  }
                  inputProps={{
                    'aria-label': 'controlled',
                  }}
                />
              }
            />
          </MenuColumn>
        </MenuTwoRowWrapper>
        <MenuRowWrapper>
          <TextField
            data-testid="custom-needs-textfield"
            variant="outlined"
            label="Describe Your Needs"
            multiline
            size="small"
            rows={4}
            value={state.customNeeds}
            onChange={(e) => {
              handleChange(QuestionGenerationMenuType.SET_CUSTOM_NEEDS)(e.target.value);
            }}
            fullWidth
            disabled={generating !== null}
            placeholder="Describe any specific requirements or context for the questions you want to generate..."
          />
        </MenuRowWrapper>
        <ShowIf If={coauthorEnabled}>
          <MenuRowWrapper>
            <ShowIf If={!isSubmitting}>
              <Box sx={{ display: 'flex', gap: 2, width: '100%' }}>
                <Tooltip
                  title={
                    hasContentFilter
                      ? 'Generate questions using AI based on the selected criteria'
                      : invalidContentFilterMessage
                  }
                  arrow
                  placement="top"
                  componentsProps={toolTipStyle}
                  slotProps={{ popper: { disablePortal: true } }}
                >
                  <span style={{ flex: 1 }}>
                    <Button
                      fullWidth
                      variant="contained"
                      onClick={submitCallback}
                      disabled={generating !== null || !hasContentFilter}
                      data-testid="generate-button"
                    >
                      <ShowIf If={!generating}>Generate with AI</ShowIf>
                      <ShowIf If={generating}>
                        Generating...{' '}
                        <CircularProgress sx={{ ml: '1rem' }} color="inherit" size={20} />
                      </ShowIf>
                    </Button>
                  </span>
                </Tooltip>
                <Tooltip
                  title={
                    hasContentFilter
                      ? 'Create custom questions related to the selected criteria'
                      : invalidContentFilterMessage
                  }
                  arrow
                  placement="top"
                  componentsProps={toolTipStyle}
                  slotProps={{ popper: { disablePortal: true } }}
                >
                  <span style={{ flex: 1 }}>
                    <Button
                      fullWidth
                      variant="outlined"
                      onClick={customQuestionCallback}
                      disabled={isLoading || !hasContentFilter}
                      sx={{ fontSize: '0.85rem' }}
                    >
                      Custom Question
                    </Button>
                  </span>
                </Tooltip>
              </Box>
            </ShowIf>
          </MenuRowWrapper>
        </ShowIf>
        <ShowIf If={!coauthorEnabled}>
          <MenuRowWrapper>
            <Typography variant="body1" color="error" sx={{ textAlign: 'center' }}>
              The CoAuthor Feature is currently not available for this curriculum.
              Try a different curriculum or contact support for assistance.
            </Typography>
          </MenuRowWrapper>
        </ShowIf>
        <ShowIf If={isDisabled}>
          <MenuRowWrapper>
            <Tooltip
              title="Removes all questions and resets the form to its initial state"
              arrow
              placement="top"
              componentsProps={toolTipStyle}
            >
              <span>
                <Button
                  fullWidth
                  variant="contained"
                  color="error"
                  onClick={resetCallback}
                >
                  Reset
                </Button>
              </span>
            </Tooltip>
          </MenuRowWrapper>
        </ShowIf>
      </Box>
    </ShadowScrollbar>
  );
};

export default QuestionGenerationMenu;
